import { buildApiUrl } from "../../../../lib/utils";

export type HeaderSearchResult = {
  id: string;
  category: string;
  title: string;
  subtitle?: string;
  path: string;
};

export type HeaderPageLink = {
  id: string;
  label: string;
  path: string;
  keywords?: string[];
  permission?: string;
};

export const ADMIN_PAGE_LINKS: HeaderPageLink[] = [
  { id: "dashboard", permission: "Dashboard", label: "Dashboard", path: "/admin", keywords: ["home", "overview"] },
  {
    id: "vehicles", permission: "Vehicles",
    label: "All vehicles",
    path: "/admin/vehicles",
    keywords: ["fleet", "car", "bus", "plate"],
  },
  {
    id: "vehicle-assignments", permission: "VehicleAssignments",
    label: "Vehicle assignment",
    path: "/admin/vehicle-assignments",
    keywords: ["assign"],
  },
  {
    id: "on-going-trips", permission: "Dispatch",
    label: "Ongoing trips",
    path: "/admin/on-going-trips",
    keywords: ["trip", "dispatch"],
  },
  {
    id: "dispatch-import", permission: "Dispatch",
    label: "Dispatch import",
    path: "/admin/dispatch-import",
    keywords: ["dispatch", "import", "upload", "loading sheet", "excel"],
  },
  {
    id: "drivers", permission: "Company",
    label: "All drivers",
    path: "/admin/drivers",
    keywords: ["driver"],
  },
  {
    id: "crew-members", permission: "Company",
    label: "Crew members",
    path: "/admin/crew-members",
    keywords: ["crew", "staff"],
  },
  { id: "fines", permission: "Company", label: "Fines", path: "/admin/fines", keywords: ["penalty"] },
  {
    id: "partners", permission: "Company",
    label: "Partners",
    path: "/admin/partners",
    keywords: ["partner"],
  },
  {
    id: "maintenance", permission: "Maintenance",
    label: "Maintenance",
    path: "/admin/maintenance",
    keywords: ["repair", "service"],
  },
  { id: "fuel", permission: "Maintenance", label: "Fuel logs", path: "/admin/fuel", keywords: ["gas"] },
  {
    id: "requisition", permission: "Requisition",
    label: "Requisition",
    path: "/admin/requisition",
    keywords: ["request", "approval"],
  },
  {
    id: "finance", permission: "Company",
    label: "Finance",
    path: "/admin/finance",
    keywords: ["income", "expense", "money"],
  },
  {
    id: "incident-report", permission: "Company",
    label: "Incident report",
    path: "/admin/incident-report",
    keywords: ["accident"],
  },
  {
    id: "users", permission: "Users",
    label: "User management",
    path: "/admin/users",
    keywords: ["staff", "team"],
  },
  {
    id: "settings", permission: "Company",
    label: "Settings",
    path: "/admin/settings",
    keywords: ["profile", "account"],
  },
];

export const SUPER_ADMIN_PAGE_LINKS: HeaderPageLink[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/super-admin",
    keywords: ["home", "overview"],
  },
  {
    id: "users",
    label: "User management",
    path: "/super-admin/users",
    keywords: ["staff", "team"],
  },
  {
    id: "companies",
    label: "Companies",
    path: "/super-admin/companies",
    keywords: ["company", "tenant"],
  },
  {
    id: "subscription",
    label: "Subscription",
    path: "/super-admin/subscription",
    keywords: ["plan"],
  },
  {
    id: "custom-pricing",
    label: "Custom pricing",
    path: "/super-admin/custom-pricing",
    keywords: ["price", "plan"],
  },
  {
    id: "transaction",
    label: "Transactions",
    path: "/super-admin/transaction",
    keywords: ["payment"],
  },
  {
    id: "vehicle-brands",
    label: "Vehicle brands",
    path: "/super-admin/vehicle-brands",
    keywords: ["brand"],
  },
  {
    id: "audit-log",
    label: "Audit log",
    path: "/super-admin/audit-log",
    keywords: ["history"],
  },
  {
    id: "settings",
    label: "Settings",
    path: "/super-admin/settings",
    keywords: ["profile", "account"],
  },
];

export function getInitials(name?: string | null): string {
  if (!name?.trim()) return "U";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function resolveImageUrl(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }
  try {
    return buildApiUrl(path);
  } catch {
    return path;
  }
}

export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 45) return "Just now";

  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;

  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;

  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
}

export function extractSearchItems<T>(payload: unknown): T[] {
  if (!payload || typeof payload !== "object") return [];
  const root = payload as { data?: unknown };
  if (Array.isArray(root.data)) return root.data as T[];
  if (root.data && typeof root.data === "object") {
    const nested = (root.data as { data?: unknown }).data;
    if (Array.isArray(nested)) return nested as T[];
  }
  return [];
}

export function extractTotalCount(payload: unknown): number {
  if (!payload || typeof payload !== "object") return 0;
  const root = payload as {
    data?: { totalCount?: number; data?: { totalCount?: number } };
  };
  return root.data?.totalCount ?? root.data?.data?.totalCount ?? 0;
}

export function matchesPageLink(page: HeaderPageLink, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [page.label, page.path, ...(page.keywords ?? [])]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}
